import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from "@angular/core";
import { PeopleService, UsersById } from "@app2/account/people.service";
import { UserService } from "@app2/account/user.service";
import { Domain } from "@app2/alert/constants";
import { AlertClientService } from "@app2/clients/alert-client.service";
import { Alert, ItemViewerIds, ItemViewerNames } from "@app2/clients/types/alert-typedefs";
import { PRODUCT_DOMAIN } from "@app2/alert/alert-inbox/injection-tokens";
import { getSort } from "@app2/alert/alert-inbox/utils";
import { InboxDropdownFilterType, SortType, StateType } from "@app2/fraud/type-defs";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { WindowService } from "@app2/shared/services/window.service";
import { DEFAULT_PAGE_SIZE } from "@app2/shared/shared-constants";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { getMessageForError } from "@app2/util/errors/handleable-errors";

@UntilDestroy()
@Component({
    selector: "alert-mode-viewer",
    template: `
        <div class="inbox-left-container">
            <header class="inbox-left-header">
                <alert-inbox-sort-dropdown [selectedSort]="sort"
                                           [state]="state">
                </alert-inbox-sort-dropdown>
            </header>
            <alert-selector [loading]="loading"
                            [alerts]="allAlerts"
                            [selectedAlertId]="selectedAlertId"
                            [viewers]="viewerNames"
                            [loadingMore]="loadingMoreAlerts"
                            [canLoadMore]="canLoadMoreAlerts"
                            emptyMessageKey="NO_MORE_ALERTS"
                            (onAlertSelected)="selectedAlertIdChange.emit($event.id)"
                            (onRequestMore)="loadMoreAlerts()">
            </alert-selector>
        </div>
        <div class="inbox-right-container">
            <single-alert-display [loading]="loading"
                                  [alertId]="selectedAlertId"
                                  (onAlertStateChanged)="onAlertStateChanged()">
            </single-alert-display>
        </div>
    `,
    host: {
        style: "display: contents;",
    },
})
export class AlertModeViewerComponent implements OnInit, OnChanges, OnDestroy {
    @Input() dropdownFilter: InboxDropdownFilterType;
    @Input() state: StateType;
    @Input() sort: SortType;
    @Input() selectedAlertId: uuid;
    @Output() selectedAlertIdChange = new EventEmitter<uuid>();

    allAlerts: Alert[] = [];
    viewerNames: ItemViewerNames = {};
    loading: boolean;
    refreshIntervalSubscription;
    people: UsersById;
    loadingMoreAlerts: boolean;
    canLoadMoreAlerts: boolean;

    private refreshEverySeconds = 4;
    private seenThresholdSeconds = 10;

    constructor(@Inject(PRODUCT_DOMAIN) private inboxDomain: Domain,
                private readonly alertClient: AlertClientService,
                private readonly userService: UserService,
                private readonly notificationsService: NotificationsService,
                private window: WindowService,
                private peopleService: PeopleService) {
        this.peopleService.getAllHomeOrgUsersWithAccessToCurrentOrgById$()
            .pipe(untilDestroyed(this))
            .subscribe(people => {
                this.people = people;
            });
    }

    ngOnInit() {
        this.refreshIntervalSubscription = this.window.setInterval(() => {
            if (this.selectedAlertId) {
                this.refreshAlertViewersAndRegisterView();
            } else {
                this.refreshAlertViewers();
            }
        }, this.refreshEverySeconds * 1000);
    }

    ngOnDestroy() {
        if (this.refreshIntervalSubscription) {
            this.window.clearInterval(this.refreshIntervalSubscription);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (((changes.state || changes.sort) && this.state && this.sort) ||
            (changes.dropdownFilter && this.dropdownFilter)) {
            this.refreshAlertList();
        }
        if (changes.selectedAlertId && this.selectedAlertId) {
            this.refreshAlertViewersAndRegisterView();
        }
    }

    refreshAlertViewersAndRegisterView(): void {
        const currentUserId = this.userService.getCurrentUser().id;
        this.alertClient.registerAndGetViewersForItems(currentUserId,
            this.selectedAlertId, this.seenThresholdSeconds, this.allAlerts.map(a => a.id))
            .then(viewers => this.viewerNames = this.getFilteredUserNames(viewers))
            .catch(() => {});
        // Empty Catch: In the case of internet disconnection, don't have any console errors
        //  since this isn't essential functionality we don't show error notification
    }

    refreshAlertViewers(): void {
        this.alertClient.getViewersForItems(this.seenThresholdSeconds, this.allAlerts.map(a => a.id))
            .then(viewers => this.viewerNames = this.getFilteredUserNames(viewers))
            .catch(() => {});
    }

    private getFilteredUserNames(itemViewerIds: ItemViewerIds): ItemViewerNames {
        // Removes own user and any users who are not in your home org
        const currentUserId = this.userService.getCurrentUser().id;
        return this.convertUserIdsToNames(this.removeUserId(itemViewerIds, currentUserId));
    }

    private convertUserIdsToNames(itemViewerIds: ItemViewerIds): ItemViewerNames {
        // Additional, hidden effect: Any userIds that don't map to Users (or don't belong to home org) are discarded
        return Object.keys(itemViewerIds).reduce((result, itemId) => {
            const filteredUserNames: string[] = itemViewerIds[itemId].filter(userId => this.people[userId])
                .map(userId => this.people[userId].fullName);
            if (filteredUserNames.length > 0) {
                result[itemId] = filteredUserNames;
            }
            return result;
        }, {});
    }

    private removeUserId(itemViewers: ItemViewerIds, removeUserId: uuid): ItemViewerIds {
        return Object.keys(itemViewers).reduce(function (result, itemId) {
            const filteredUsers: uuid[] = itemViewers[itemId].filter(userId => userId !== removeUserId);
            if (filteredUsers.length > 0) {
                result[itemId] = filteredUsers;
            }
            return result;
        }, {});
    }

    onAlertStateChanged() {
        // Select next alert, clear selected alert if no more alerts left
        this.selectedAlertIdChange.emit(this.allAlerts[this.allAlerts.findIndex(alert => alert.id === this.selectedAlertId) + 1]?.id);
        this.refreshAlertList();
    }

    loadMoreAlerts(): Promise<any> {
        let filters;
        if (!this.dropdownFilter || this.dropdownFilter === "all") {
            filters = [`state:${this.state}`];
        } else if (this.dropdownFilter === "informational") {
            filters = [`severity:${this.dropdownFilter}`, `state:${this.state}`];
        } else {
            filters = [`managedByTrac:${this.dropdownFilter}`, `state:${this.state}`];
        }
        const sort = getSort(this.sort);
        this.loadingMoreAlerts = true;

        return this.alertClient.getAlertPage(sort.desc, sort.field, this.inboxDomain, filters,
            this.allAlerts[this.allAlerts.length - 1])
            .then(newAlerts => {
                this.allAlerts = [...this.allAlerts, ...newAlerts];
                this.canLoadMoreAlerts = newAlerts.length === DEFAULT_PAGE_SIZE;
                if (!this.selectedAlertId && this.allAlerts.length > 0) {
                    this.selectedAlertId = this.allAlerts[0].id;
                    this.selectedAlertIdChange.emit(this.selectedAlertId);
                    this.refreshAlertViewersAndRegisterView();
                }
            })
            .catch(error => {
                const errorMessage = getMessageForError(error.error, "ERROR_LOADING_ALERTS");
                this.notificationsService.showError(errorMessage, error);
            })
            .finally(() => this.loadingMoreAlerts = false);
    }

    private refreshAlertList() {
        this.loading = true;
        this.allAlerts = [];
        this.loadMoreAlerts()
            .finally(() => this.loading = false);
    }
}
